import { type DateRangePickerProps } from '@eppendorf/vnls-react-components';
import { endOfDay, startOfDay } from 'date-fns';
import { useCallback, useState } from 'react';

type QuickSelectOption = NonNullable<
  NonNullable<DateRangePickerProps['quickSelect']>['selectedOption']
>;

export interface ReportDateRangeState {
  dateRange: { from?: Date; to?: Date };
  selectedQuickOption: QuickSelectOption | undefined;
  from: string | undefined;
  to: string | undefined;
  handleDateRangeChange: (newDateRange: DateRangePickerProps['dateRange']) => void;
  handleQuickSelectOption: (option: QuickSelectOption) => void;
  resetDateRange: () => void;
}

export function useReportDateRange(): ReportDateRangeState {
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [selectedQuickOption, setSelectedQuickOption] = useState<
    QuickSelectOption | undefined
  >(undefined);

  const from = dateRange.from ? startOfDay(dateRange.from).toISOString() : undefined;
  const to = dateRange.to ? endOfDay(dateRange.to).toISOString() : undefined;

  const handleDateRangeChange = useCallback(
    (newDateRange: DateRangePickerProps['dateRange']) => {
      if (newDateRange?.from || newDateRange?.to) {
        setDateRange({
          from: newDateRange?.from,
          to: newDateRange?.to,
        });
      } else {
        setDateRange({});
      }
      setSelectedQuickOption(undefined);
    },
    [],
  );

  const handleQuickSelectOption = useCallback((option: QuickSelectOption) => {
    if (option === 'custom') {
      setSelectedQuickOption(undefined);
      return;
    }
    setSelectedQuickOption(option);
  }, []);

  const resetDateRange = useCallback(() => {
    setDateRange({});
    setSelectedQuickOption(undefined);
  }, []);

  return {
    dateRange,
    selectedQuickOption,
    from,
    to,
    handleDateRangeChange,
    handleQuickSelectOption,
    resetDateRange,
  };
}
